import type {
  ChangePasswordRequest,
  CsrfTokenEnvelope,
  EmailChangeRequest,
  LoginRequest,
  LoginResponse,
  MailAccepted,
  RegisterRequest,
  User,
} from '@/types/api'
import { adoptCsrfToken, invalidateCsrfToken, request, requestData } from './client'
import { isApiError } from './problem'

/**
 * 身份相关接口。openapi.yaml tags: auth。
 *
 * ## CSRF Token 随认证状态变化
 *
 * Token 绑定当前 Cookie：登录把绑定值从 `anon:` 换成 `session:`，登出与改密码
 * 再换回 `anon:`。所以这三个响应**各自带一份新 Token**，后端为此刻意让它们返回
 * `200` 而不是 `204`（见 openapi.yaml `/auth/sessions`、`/auth/session`、
 * `/me/password` 的说明）。这里直接 `adoptCsrfToken` 采纳，不再回头去
 * `GET /auth/csrf-token` —— 那一次往返在响应体里已经付过了。
 *
 * 同一条规则适用于下面三条 `*-confirmations`：它们与登录同形，也各自带一份新
 * Token。漏掉 `adoptCsrfToken` 的症状很隐蔽 —— `client.ts` 的 403 重试会救回来
 * 一次，于是这个 bug 只表现为多一次 `GET /auth/csrf-token` 的往返。
 *
 * 改密码还会递增 `session_version`，让**全部**会话立即失效，包括发起请求的这一个。
 * 三条 confirmations 同样递增它：用户在其他设备上会被登出，这是设计。
 *
 * ## 「提交注册」不会登录，「确认注册」会
 *
 * `POST /auth/registrations` **既不建会话也不建账号**：它只写入一条待验证注册并
 * 安排一封验证信，响应是 `202 MailAccepted`。账号在
 * `POST /auth/registration-confirmations` 才诞生，而那一条在**同一个事务里**
 * 下发会话（注册即登录）。所以提交注册之后用户仍是游客，且没有账号可登录 ——
 * 不要在它后面补一次 `login`。见 stores/AuthProvider.tsx。
 */

/* -------------------------------------------------------- 四条「会发信」端点 */

/**
 * 提交注册：写入一条待验证注册并安排一封验证信。
 *
 * **不创建账号，也不建立会话**，因此返回 `202 MailAccepted` 而不是 `201 User`。
 * `MailAccepted` 里只有链接有效期，读不出「该邮箱是否已注册」—— 那件事只改变
 * 发出去的那封信。用户名冲突仍然明说 `409 state_conflict`（用户名在作者页与
 * 审计里本来就公开），**只有邮箱不可枚举**。
 *
 * 部署启用人机验证时必须带 `turnstileToken`（widget 的 action 为 `register`），
 * 缺失返回 `403 challenge_failed`。token 一次性：**每次提交都要一个新的**，
 * 失败后要重置 widget 再取，见 api/turnstile.ts。
 */
export function register(
  payload: RegisterRequest,
  options: { turnstileToken?: string; signal?: AbortSignal } = {},
): Promise<MailAccepted> {
  return requestData<MailAccepted>('/auth/registrations', {
    method: 'POST',
    body: payload,
    ...(options.turnstileToken ? { turnstileToken: options.turnstileToken } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

/**
 * 重发注册验证信。
 *
 * 三种情形响应完全相同（恒定 `202`）：有待验证的注册 → **轮换令牌，上一封信里
 * 的链接当场失效**；该邮箱已属活账号 → 发一封「你已有账号」；什么都没有 →
 * 一封信都不发。界面因此必须写清「请使用最新一封邮件里的链接」，否则用户会去点
 * 旧邮件然后拿到 `422 /token expired|invalid`。
 *
 * action 是 `verification_email`，**不是** `register`：后端逐路由比 action，
 * 拿注册页的 token 来刷这里会被判 `rejected`。
 */
export function resendRegistrationVerification(
  email: string,
  options: { turnstileToken?: string; signal?: AbortSignal } = {},
): Promise<MailAccepted> {
  return requestData<MailAccepted>('/auth/registration-verifications', {
    method: 'POST',
    body: { email },
    ...(options.turnstileToken ? { turnstileToken: options.turnstileToken } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

/**
 * 申请重设口令。恒定 `202`：**邮箱不存在时一封信都不发，也不报错。**
 *
 * 所以调用方的终态文案只能写「如果该邮箱在本站有账号，重置链接已经发出」——
 * 写成「重置邮件已发送」就是在断言一件服务器没告诉你的事。
 *
 * 再申请一次会轮换令牌，上一封信里的链接当场失效。action 是 `password_reset`。
 */
export function requestPasswordReset(
  email: string,
  options: { turnstileToken?: string; signal?: AbortSignal } = {},
): Promise<MailAccepted> {
  return requestData<MailAccepted>('/auth/password-resets', {
    method: 'POST',
    body: { email },
    ...(options.turnstileToken ? { turnstileToken: options.turnstileToken } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

/**
 * 申请修改邮箱。需要登录**并且**需要当前口令，因此不做人机验证。
 *
 * 新邮箱在确认之前**不写进账号**，所以这里成功只意味着「确认信已安排发往新地址」。
 * 新邮箱已被别人占用时**也返回 `202` 且不发确认信** —— 否则登录用户就有了一个
 * 邮箱枚举接口。因此「点了没反应」是一种合法结果，文案不能承诺一定收得到。
 *
 * 两种可明说的失败：与当前邮箱相同是 `422 /email unchanged`；`current_password`
 * 不正确是 **`401 authentication_required`，那不是会话过期** —— 调用点必须把它
 * 当作字段级错误挂在密码框上，绝不能顺手 `markAnonymous()` 把用户登出。
 */
export function requestEmailChange(
  payload: EmailChangeRequest,
  options: { signal?: AbortSignal } = {},
): Promise<MailAccepted> {
  return requestData<MailAccepted>('/me/email-changes', {
    method: 'POST',
    body: payload,
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

/* ------------------------------------------ 三条 confirmations（消费邮件令牌） */

/*
  它们与 `login()` 同形，而且必须同形：响应体里是用户与一份**新** CSRF Token
  （绑定值刚从 `anon:` 换成 `session:`），会话 Cookie 后端已经写好。所以
  `adoptCsrfToken` 不是可选的收尾，而是契约的一部分；调用方拿到 `User` 之后
  还要交给 AuthProvider 的 `adoptSession`，让全局状态跟上这个已经存在的会话。

  令牌失败一律 `422`，字段 `/token`，`code` 取 `invalid` / `expired` /
  `consumed`。`consumed` **刻意不隐藏**：能拿到令牌的人本来就持有它，而这条提示
  正是「引导他直接去登录」的依据。
*/

/**
 * 邮件令牌失败的三种成因，外加「用户名/邮箱被抢走」这一种。
 *
 * 抽出来是因为三个令牌落地页要按它分文案，而每一种的**下一步动作都不同**：
 * 前两种要重新申请一封信，`consumed` 要直接去登录，`taken` 要换一个用户名。
 * 按 code 分支而不是按 detail 文本 —— 后者是后端面向开发者写的。
 */
export type MailTokenFailure = 'invalid' | 'expired' | 'consumed' | 'taken'

/**
 * 从异常里取出邮件令牌的失败成因；不是这类失败时返回 undefined。
 *
 * `409 state_conflict` 归入 `taken`：它不是令牌的问题（令牌恰恰是好的），
 * 而是「等待窗口里用户名 / 目标邮箱被别人注册走了」。两者放在同一个枚举里，
 * 是因为落地页要在同一处做穷尽分支，漏掉 `409` 的表现是页面显示一句
 * 与病因无关的「链接无效」。
 */
export function mailTokenFailure(error: unknown): MailTokenFailure | undefined {
  if (!isApiError(error)) return undefined
  if (error.code === 'state_conflict') return 'taken'
  if (error.code !== 'validation_failed') return undefined
  const code = error.fieldError('/token')?.code
  if (code === 'invalid' || code === 'expired' || code === 'consumed') return code
  // 后端加了新的字段码时按「无效」处理：那条文案的下一步是「重新申请一封」，
  // 对任何一种未知的令牌故障都成立。
  return code ? 'invalid' : undefined
}

/**
 * 完成注册：创建账号并直接登录（账号、审计、会话在同一个事务里）。
 *
 * `409 state_conflict` 的含义是「待验证窗口里用户名被别人注册走了」——
 * 用户名刻意不预占，所以这条真的会发生，界面要让他换一个用户名重新注册。
 */
export async function confirmRegistration(token: string, signal?: AbortSignal): Promise<User> {
  const { csrf, ...user } = await requestData<LoginResponse>('/auth/registration-confirmations', {
    method: 'POST',
    body: { token },
    ...(signal ? { signal } : {}),
  })
  adoptCsrfToken(csrf)
  return user
}

/**
 * 用令牌设置新口令。撤销**全部**既有会话，同时给本次调用者签发一个新的。
 *
 * 冻结或已删除的账号返回 `422 /token invalid`，与「链接无效」一致 ——
 * 后端刻意不暴露「该账号存在但被冻结」，所以前端也只能照「链接无效」讲。
 */
export async function confirmPasswordReset(
  token: string,
  password: string,
  signal?: AbortSignal,
): Promise<User> {
  const { csrf, ...user } = await requestData<LoginResponse>('/auth/password-reset-confirmations', {
    method: 'POST',
    body: { token, password },
    ...(signal ? { signal } : {}),
  })
  adoptCsrfToken(csrf)
  return user
}

/**
 * 确认新邮箱。落地新邮箱、撤销全部旧会话，并给本次调用者签发新会话。
 *
 * `409 state_conflict` 是「申请之后那个地址被别人注册走了」。这里可以明说：
 * 调用者刚证明了自己控制该邮箱，告诉他「已被占用」没有泄露他不知道的事。
 */
export async function confirmEmailChange(token: string, signal?: AbortSignal): Promise<User> {
  const { csrf, ...user } = await requestData<LoginResponse>('/auth/email-change-confirmations', {
    method: 'POST',
    body: { token },
    ...(signal ? { signal } : {}),
  })
  adoptCsrfToken(csrf)
  return user
}

/* ------------------------------------------------------------ 会话与账号自身 */

export async function login(payload: LoginRequest, signal?: AbortSignal): Promise<User> {
  const { csrf, ...user } = await requestData<LoginResponse>('/auth/sessions', {
    method: 'POST',
    body: payload,
    ...(signal ? { signal } : {}),
  })
  adoptCsrfToken(csrf)
  return user
}

export async function logout(signal?: AbortSignal): Promise<void> {
  try {
    const { csrf } = await requestData<CsrfTokenEnvelope>('/auth/session', {
      method: 'DELETE',
      ...(signal ? { signal } : {}),
    })
    adoptCsrfToken(csrf)
  } catch (error) {
    // 登出失败时会话状态不确定，手里的 Token 也不能再信 —— 丢掉重取。
    invalidateCsrfToken()
    throw error
  }
}

/** 当前用户。未登录时后端返回 401 —— 调用方应把它当作「游客」而不是错误。 */
export async function fetchCurrentUser(
  signal?: AbortSignal,
): Promise<{ user: User; etag: string | undefined }> {
  const { data, etag } = await request<User>('/me', { ...(signal ? { signal } : {}) })
  return { user: data, etag }
}

/** 修改显示名。版本化实体：必须带 If-Match，缺失 428、过期 412。 */
export async function updateDisplayName(
  displayName: string,
  etag: string,
  signal?: AbortSignal,
): Promise<{ user: User; etag: string | undefined }> {
  const { data, etag: nextEtag } = await request<User>('/me', {
    method: 'PATCH',
    body: { display_name: displayName },
    ifMatch: etag,
    ...(signal ? { signal } : {}),
  })
  return { user: data, etag: nextEtag }
}

/**
 * 修改密码。
 *
 * 成功后**全部会话失效**（含当前这一个），响应清除会话 Cookie 并交还一份绑定
 * 匿名标识的新 Token。调用方随后必须把界面切回游客态并要求重新登录。
 */
export async function changePassword(
  payload: ChangePasswordRequest,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const { csrf } = await requestData<CsrfTokenEnvelope>('/me/password', {
      method: 'PUT',
      body: payload,
      ...(signal ? { signal } : {}),
    })
    adoptCsrfToken(csrf)
  } catch (error) {
    invalidateCsrfToken()
    throw error
  }
}
